import React, { useEffect, useState } from 'react';
import api from '../config/axiosConfig';
import { toast } from 'react-toastify';
import { FaPaperPlane, FaBullhorn, FaTrash } from 'react-icons/fa';

interface AnnouncementRow {
    id: number;
    title: string | null;
    message: string;
    target_role: string | null;
    type: string | null;
    expires_at: string | null;
    created_at: string;
}

const AdminAnnouncementsPage: React.FC = () => {
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        targetRole: 'client',
        type: 'promotion',
        durationDays: '7'
    });
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<AnnouncementRow[]>([]);

    const loadRows = async () => {
        try {
            const res = await api.get<{ success: boolean; data: AnnouncementRow[] }>('/api/notifications/announcements');
            setRows(res.data.data || []);
        } catch {
            setRows([]);
        }
    };

    useEffect(() => {
        loadRows();
    }, []);

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.message.trim()) return toast.error('El mensaje es obligatorio');

        setLoading(true);
        try {
            await api.post('/api/notifications/announce', {
                ...formData,
                durationDays: formData.durationDays ? Number(formData.durationDays) : null
            });
            toast.success('Aviso enviado.');
            setFormData({ title: '', message: '', targetRole: 'client', type: 'promotion', durationDays: '7' });
            loadRows();
        } catch {
            toast.error('Error al enviar el anuncio.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('¿Eliminar este aviso? Deja de verse en la campanita de quien lo recibió.')) return;
        try {
            await api.delete(`/api/notifications/announcements/${id}`);
            toast.success('Aviso eliminado.');
            setRows((prev) => prev.filter((row) => row.id !== id));
        } catch {
            toast.error('No se pudo eliminar el aviso.');
        }
    };

    return (
        <div className="container mx-auto p-6">
            <h1 className="text-3xl font-bold mb-6 text-gray-800 flex items-center gap-2">
                <FaBullhorn className="text-indigo-600" /> Centro de Novedades y Ofertas
            </h1>

            <div className="bg-white p-8 rounded-lg shadow-md border border-gray-200 max-w-3xl">
                <form onSubmit={handleSend} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Destinatarios</label>
                            <select
                                value={formData.targetRole}
                                onChange={(e) => setFormData({ ...formData, targetRole: e.target.value })}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="client">Solo Clientes</option>
                                <option value="agent">Solo Agentes</option>
                                <option value="all">Todos los Usuarios</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Tipo de Aviso</label>
                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="promotion">Oferta / Promo (Pop-up)</option>
                                <option value="alert">Alerta importante (Pop-up)</option>
                                <option value="info">Información (solo campanita)</option>
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Días que queda visible</label>
                        <input
                            type="number"
                            min="1"
                            value={formData.durationDays}
                            onChange={(e) => setFormData({ ...formData, durationDays: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                            placeholder="Vacío = no vence"
                        />
                        <p className="text-xs text-gray-500 mt-1">Si lo dejás vacío, el aviso no vence solo. Siempre se puede borrar desde el listado.</p>
                    </div>

                    {(formData.type === 'promotion' || formData.type === 'alert') && (
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Título del Pop-up</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                placeholder="Ej: Descuento exclusivo"
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Mensaje</label>
                        <textarea
                            value={formData.message}
                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg h-32 focus:ring-2 focus:ring-indigo-500 resize-none"
                            placeholder="Escribí el contenido del aviso..."
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-3 px-6 rounded-lg text-white font-bold text-lg transition-all flex justify-center items-center gap-2 ${
                            loading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700 shadow-md hover:shadow-lg'
                        }`}
                    >
                        <FaPaperPlane /> {loading ? 'Enviando...' : 'Enviar Anuncio'}
                    </button>
                </form>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200 max-w-3xl mt-6">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Avisos enviados</h2>
                {rows.length === 0 ? (
                    <p className="text-gray-500 text-sm">Todavía no hay avisos en el listado.</p>
                ) : (
                    <ul className="space-y-3">
                        {rows.map((row) => (
                            <li key={row.id} className="border border-gray-200 rounded-lg p-4 flex justify-between gap-4">
                                <div>
                                    <p className="font-semibold text-gray-800">{row.title || 'Sin título'}</p>
                                    <p className="text-sm text-gray-600 mt-1">{row.message}</p>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Para: {row.target_role || 'todos'}
                                        {row.expires_at ? ` · Vence: ${String(row.expires_at).slice(0, 16).replace('T', ' ')}` : ' · Sin vencimiento'}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(row.id)}
                                    className="text-red-600 hover:text-red-800 shrink-0 flex items-center gap-1 text-sm"
                                >
                                    <FaTrash /> Eliminar
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default AdminAnnouncementsPage;
